<script setup lang="ts">
import {
  DATA_QA,
  ValidationErrorType,
} from './dnc-import-details-step.constants';
import { DtNotice } from '@dialpad/dialtone/vue3/lib/notice';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DtStack } from '@dialpad/dialtone/vue3/lib/stack';
import { DtIcon } from '@dialpad/dialtone/vue3/lib/icon';
import { DtInput } from '@dialpad/dialtone/vue3/lib/input';
import { nextTick, onMounted, provide, ref, watch } from 'vue';
import { useI18N } from '@dialpad/i18n';
import { usePhoneCsvStore } from '@/stores';
import type { PhoneCsv } from '@/apis/generated/dialbunny';
import logger from '@/logger';
import { TRIGGER_FORM_VALIDATION } from '@/app/utils/keys';

const log = logger.getChild('DncImportDetailsStep');
const MAX_NAME_LENGTH = 50;

log.debug('Mounting');

onMounted(() => {
  log.debug('Mounted');
});

const { $t } = useI18N();

const props = defineProps<{
  dncCsv: PhoneCsv;
}>();

const emit = defineEmits(['continue', 'back']);

const validationTrigger = ref(0);
provide(TRIGGER_FORM_VALIDATION, validationTrigger);

const loading = ref(false);
const errors = ref(new Map<string, ValidationErrorType | null>());
const name = ref('');
const phoneCsvStore = usePhoneCsvStore();

const updateName = () => {
  name.value =
    props.dncCsv.name ??
    props.dncCsv.filename?.replace('.csv', '') ??
    'DNC Import';
};

updateName();

watch(() => [props.dncCsv.name, props.dncCsv.filename], updateName);

const setError = (key: string, error: ValidationErrorType | null) => {
  log.debug('Setting error', key, error);
  errors.value.set(key, error);
};

const validate = async (): Promise<boolean> => {
  log.debug('Validating..');

  await nextTick();

  if (!name.value || name.value.trim().length === 0) {
    setError('name', ValidationErrorType.REQUIRED);
    nameError.value = $t('DNC_NAME_INPUT_REQUIRED');
  } else if (name.value.length > MAX_NAME_LENGTH) {
    setError('name', ValidationErrorType.TOO_LONG);
    nameError.value = $t('DNC_IMPORT_NAME_TOO_LONG');
  } else {
    setError('name', null);
    nameError.value = null;
  }

  const isValid = [...errors.value.values()].every((error) => error === null);

  log.debug('Validation result:', isValid);
  return isValid;
};

const handleImportError = async (error: unknown) => {
  log.error('Import error:', error);
};

const submit = async () => {
  log.debug('Submitting..');
  if (!(await validate())) {
    return;
  }

  loading.value = true;
  log.debug('Starting phone CSV import...');

  phoneCsvStore.update(
    props.dncCsv.id,
    {
      name: name.value,
      action: 'import',
    },
    {
      onSuccess: async () => {
        log.debug('Import started successfully, refreshing store...');
        await phoneCsvStore.refresh();
        log.debug('Store refreshed, emitting continue');
        emit('continue');
        loading.value = false;
      },
      onRetry: () => {},
      onError: async (error) => {
        await handleImportError(error);
        loading.value = false;
      },
    },
  );
};

watch([validationTrigger], () => {
  void validate();
});

const nameError = ref<string | null>(null);
</script>
<template>
  <div :data-qa="DATA_QA.ROOT">
    <p class="d-fc-secondary">
      {{ $t('DNC_IMPORT_DETAILS_RECORDS_TO_IMPORT') }}
    </p>
    <p
      :data-qa="DATA_QA.ROW_COUNT"
      class="d-fc-primary d-fs-500 d-fw-medium d-mt-16"
    >
      {{ props.dncCsv.numRows ?? 0 }}
    </p>
    <DtNotice :hide-close="true" class="d-wmx100p">
      <span>{{ $t('DNC_IMPORT_DETAILS_NOTICE_BODY') }}</span>
      <template #icon>
        <DtIcon name="info" size="400" />
      </template>
    </DtNotice>
    <div class="d-mt16">
      <DtInput
        v-model="name"
        :data-qa="DATA_QA.NAME_INPUT"
        :label="$t('DNC_IMPORT_NAME_LABEL')"
        :placeholder="$t('DNC_IMPORT_NAME_PLACEHOLDER')"
        :messages="nameError ? [{ message: nameError, type: 'error' }] : []"
        :maxlength="MAX_NAME_LENGTH"
        required
      />
      <p class="d-fs-100 d-fc-tertiary d-mt4">
        {{ $t('DNC_IMPORT_NAME_HELP') }}
      </p>
    </div>

    <div class="d-d-flex d-jc-flex-end d-mt16">
      <DtStack direction="row" gap="400">
        <DtButton
          :data-qa="DATA_QA.BACK"
          importance="clear"
          @click="emit('back')"
        >
          {{ $t('DNC_IMPORT_BACK') }}
        </DtButton>
        <DtButton
          :data-qa="DATA_QA.IMPORT"
          :loading="loading"
          kind="primary"
          @click="submit"
        >
          {{ $t('DNC_IMPORT_IMPORT') }}
        </DtButton>
      </DtStack>
    </div>
  </div>
</template>
